// src/hooks/useBottomSheetDrag.js
import { useEffect, useRef, useCallback } from "react";

/**
 * Bottom sheet drag-to-close (mobile)
 * - Fixes freezes by listening on the capture target (sheet) and handling lostpointercapture
 * - Prevents page navigation by never disabling overlay pointer-events
 * - Disambiguates tap vs drag and blocks the "ghost click" after a drag
 */
export default function useBottomSheetDrag({
  enabled,
  sheetRef,
  overlayRef,
  onClose,
  options = { startThresholdPx: 8, fadeBackdrop: false } // you can pass {fadeBackdrop:true}
}) {
  const { startThresholdPx = 8, fadeBackdrop = false } = options;

  const draggingRef = useRef(false);      // actively translating sheet
  const movedRef = useRef(false);         // passed threshold → considered a drag (not a tap)
  const startYRef = useRef(0);
  const lastYRef = useRef(0);
  const lastTRef = useRef(0);
  const velocityRef = useRef(0);          // px/ms
  const currentYRef = useRef(0);
  const rafRef = useRef(null);
  const pointerIdRef = useRef(null);
  const watchdogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // one-shot click shield to stop ghost clicks after drags
  const armClickShield = useCallback(() => {
    const handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      document.removeEventListener("click", handler, true);
    };
    // capture phase so we intercept before app handlers
    document.addEventListener("click", handler, true);
    // auto-remove if nothing clicked within a tick
    setTimeout(() => {
      document.removeEventListener("click", handler, true);
    }, 250);
  }, []);

  const setTransforms = useCallback((y) => {
    const sheet = sheetRef.current;
    const overlay = overlayRef.current;
    if (!sheet || !overlay) return;

    const rubber = y < 0 ? y * 0.25 : y; // slight rubber band upwards
    sheet.style.transform = `translate3d(0, ${rubber}px, 0)`;

    if (fadeBackdrop) {
      const h = sheet.getBoundingClientRect().height || 1;
      const fade = Math.max(0, Math.min(1, 1 - Math.max(0, y) / (h * 0.9)));
      overlay.style.opacity = String(0.25 + 0.75 * fade);
    }
  }, [sheetRef, overlayRef, fadeBackdrop]);

  const frameTo = useCallback((y) => {
    if (rafRef.current == null) {
      currentYRef.current = y;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        setTransforms(currentYRef.current);
      });
    } else {
      currentYRef.current = y;
    }
  }, [setTransforms]);

  // Listener management (declared before use)
  const listenersRef = useRef({
    moveW: null, upW: null, cancelW: null,
    moveS: null, upS: null, cancelS: null, lostCaptureS: null
  });
  const endOnceRef = useRef(false);

  const detachLiveListeners = useCallback(() => {
    const sheet = sheetRef.current;

    if (listenersRef.current.moveW) {
      window.removeEventListener('pointerup', listenersRef.current.upW);
      window.removeEventListener('pointercancel', listenersRef.current.cancelW);
    }

    if (sheet && listenersRef.current.moveS) {
      sheet.removeEventListener('pointermove', listenersRef.current.moveS);
      sheet.removeEventListener('lostpointercapture', listenersRef.current.lostCaptureS);
    }

    listenersRef.current = { moveW: null, upW: null, cancelW: null, moveS: null, upS: null, cancelS: null, lostCaptureS: null };
  }, [sheetRef]);

  const endDrag = useCallback((finalize) => {
    if (endOnceRef.current) return;
    endOnceRef.current = true;
    const sheet = sheetRef.current;
    const overlay = overlayRef.current;
    draggingRef.current = false;

    if (sheet && pointerIdRef.current != null) {
      try { sheet.releasePointerCapture(pointerIdRef.current); } catch {}
    }
    pointerIdRef.current = null;

    // cleanup listeners
    detachLiveListeners();
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }

    if (!sheet) return;

    // Avoid ghost click if there was a real drag
    if (movedRef.current) armClickShield();

    const y = currentYRef.current;
    const v = velocityRef.current; // px/ms
    const h = sheet.getBoundingClientRect().height || 1;

    const distanceThreshold = Math.min(h * 0.33, 280);
    const velocityThreshold = 1.2; // ~1200 px/s

    const shouldClose = y > distanceThreshold || v > velocityThreshold;

    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const sheetTrans = prefersReduced
      ? 'transform 120ms ease-out'
      : 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)';
    const overlayTrans = prefersReduced ? 'opacity 120ms ease-out' : 'opacity 220ms ease-out';

    sheet.style.transition = sheetTrans;
    if (overlay && fadeBackdrop) overlay.style.transition = overlayTrans;

    // Never disable overlay pointer events (prevents click-through)
    // sheet pointerEvents should be restored after anim
    sheet.style.pointerEvents = 'auto';

    if (shouldClose) {
      // Animate off-screen, then unmount
      sheet.style.transform = `translate3d(0, ${h + 32}px, 0)`;
      if (overlay && fadeBackdrop) overlay.style.opacity = '0';

      const onEnd = (ev) => {
        if (ev?.target !== sheet || ev?.propertyName !== 'transform') return;
        sheet.removeEventListener('transitionend', onEnd);
        // reset transitional styles so the next open is crisp
        sheet.style.transition = '';
        if (overlay && fadeBackdrop) overlay.style.transition = '';
        onCloseRef.current?.();
      };
      sheet.addEventListener('transitionend', onEnd);

      // watchdog in case transitionend is lost
      watchdogRef.current = setTimeout(() => {
        try { sheet.removeEventListener('transitionend', onEnd); } catch {}
        sheet.style.transition = '';
        if (overlay && fadeBackdrop) overlay.style.transition = '';
        onCloseRef.current?.();
      }, 450);
    } else {
      // Snap back
      sheet.style.transform = 'translate3d(0, 0px, 0)';
      if (overlay && fadeBackdrop) overlay.style.opacity = '';
      const onEnd = () => {
        sheet.removeEventListener('transitionend', onEnd);
        sheet.style.transition = '';
        if (overlay && fadeBackdrop) overlay.style.transition = '';
      };
      sheet.addEventListener('transitionend', onEnd);
      watchdogRef.current = setTimeout(() => {
        try { sheet.removeEventListener('transitionend', onEnd); } catch {}
        sheet.style.transition = '';
        if (overlay && fadeBackdrop) overlay.style.transition = '';
      }, 450);
    }

    // reset session flags
    movedRef.current = false;
  }, [sheetRef, overlayRef, armClickShield, fadeBackdrop, detachLiveListeners]);

  const onPointerMove = useCallback((e) => {
    if (!draggingRef.current) return;
    const y = e.clientY;
    if (typeof y !== 'number') return;

    const now = performance.now();
    const dy = y - (lastYRef.current || y);
    const dt = Math.max(1, now - (lastTRef.current || now));
    velocityRef.current = dy / dt; // px/ms
    lastYRef.current = y;
    lastTRef.current = now;

    const delta = y - startYRef.current;

    // Promote to "dragging" only after crossing threshold
    if (!movedRef.current && Math.abs(delta) >= startThresholdPx) {
      movedRef.current = true;
    }

    // Only translate once it's considered a drag. Before that, do nothing.
    if (movedRef.current) frameTo(delta);
  }, [startThresholdPx, frameTo]);

  const endDragFinalize = useCallback(() => endDrag(true), [endDrag]);
  const endDragCancel = useCallback(() => endDrag(false), [endDrag]);

  // Listener management

  const attachLiveListeners = useCallback(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    // Attach to the capture target (sheet)
    listenersRef.current.moveS = onPointerMove;
    listenersRef.current.lostCaptureS = endDragCancel;

    sheet.addEventListener('pointermove', onPointerMove, { passive: false });
    sheet.addEventListener('lostpointercapture', endDragCancel, { passive: true });

    // Window fallback (defensive)
    listenersRef.current.moveW = onPointerMove;
    listenersRef.current.upW = endDragFinalize;
    listenersRef.current.cancelW = endDragCancel;
    window.addEventListener('pointerup', endDragFinalize, { passive: true, once: true });
    window.addEventListener('pointercancel', endDragCancel, { passive: true, once: true });
  }, [sheetRef, onPointerMove, endDragFinalize, endDragCancel]);

  

  const bindHandle = {
    onPointerDown: (e) => {
      if (!enabled) return;
      if (e.pointerType === 'mouse') return;
      if (!e.isPrimary) return;

      const y = e.clientY;
      if (typeof y !== 'number') return;

      const sheet = sheetRef.current;
      const overlay = overlayRef.current;
      if (!sheet || !overlay) return;

      // Reset state for new gesture
      endOnceRef.current = false;
      draggingRef.current = true;
      movedRef.current = false;
      startYRef.current = y;
      lastYRef.current = y;
      lastTRef.current = performance.now();
      velocityRef.current = 0;
      currentYRef.current = 0;

      // Remove transitions during drag
      sheet.style.transition = 'none';
      if (overlay && fadeBackdrop) overlay.style.transition = 'none';

      // Ensure sheet is interactive during drag
      sheet.style.pointerEvents = 'auto';

      // Capture this pointer so move/up keep coming to the sheet
      try {
        pointerIdRef.current = e.pointerId;
        sheet.setPointerCapture?.(e.pointerId);
      } catch {}

      // Prevent page scroll kick-in
      e.preventDefault();

      attachLiveListeners();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      detachLiveListeners();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (watchdogRef.current) clearTimeout(watchdogRef.current);
    };
  }, [detachLiveListeners]);

  return { bindHandle };
}
